var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Transition } from 'react-transition-group';
import { tabbable } from 'tabbable';
import { useIsMounted, useResponsiveProps } from '@core-front/helper';
import { useLabels } from '@core-front/label-provider';
import { useTheme } from '@core-front/theme-provider';
import ModalContent from '../modal-content/modal-content';
import ModalFooter from '../modal-footer/modal-footer';
import { ModalContext } from '../modal-provider/modal-provider-core';
const ModalCore = (props) => {
    const labels = useLabels();
    const context = React.useContext(ModalContext);
    const { responsive } = props, rest = __rest(props, ["responsive"]);
    return responsive ? (React.createElement(ModalResponsiveCore, Object.assign({}, rest, { responsive: responsive, labels: labels, context: context }))) : (React.createElement(ModalInnerCore, Object.assign({}, rest, { labels: labels, context: context })));
};
export const ModalResponsiveCore = (props) => {
    const { responsive } = props, rest = __rest(props, ["responsive"]);
    const responsiveProps = useResponsiveProps({
        responsive,
    });
    return React.createElement(ModalInnerCore, Object.assign({}, rest, responsiveProps));
};
const ModalInnerCore = (props) => {
    const { isClosable = true, isOpen, initialFocusedElementId, onClose, size, verticalAlign, className, sidebar, isFullHeight, isFullScreen, children, headerType, closeButtonLabel, components: { CloseButton, Spinner }, asideContent, hasContentComponent, } = props;
    const { portalRoot } = useTheme();
    const context = React.useContext(ModalContext);
    let modal = null;
    const [disablePageInteraction, setDisablePageInteraction] = React.useState(false);
    const isMounted = useIsMounted();
    const isModalOpen = React.useCallback(() => {
        return typeof isOpen !== 'undefined' ? isOpen : context.isOpen;
    }, [context.isOpen, isOpen]);
    const hijackScrollAndPageAccess = React.useCallback(() => {
        if (!disablePageInteraction) {
            setDisablePageInteraction(true);
        }
    }, [disablePageInteraction]);
    React.useEffect(() => {
        if (disablePageInteraction && modal) {
            if (initialFocusedElementId) {
                const initialFocusedElement = modal.querySelector(`#${initialFocusedElementId}`);
                if (initialFocusedElement) {
                    initialFocusedElement.focus();
                    return;
                }
            }
            const firstTabbableElement = tabbable(modal)[0];
            if (firstTabbableElement) {
                firstTabbableElement.focus();
                return;
            }
            modal.focus();
        }
    }, [disablePageInteraction, initialFocusedElementId, modal]);
    const close = (event) => {
        event.preventDefault();
        if (onClose) {
            onClose(event);
        }
        context.close(event);
    };
    React.useEffect(() => {
        if (isMounted && isModalOpen()) {
            hijackScrollAndPageAccess();
        }
    }, [hijackScrollAndPageAccess, isModalOpen, isMounted, disablePageInteraction]);
    const onKeyDown = (event) => {
        switch (event.key) {
            // Invoke onClose when user hits ESC key.
            case 'Escape':
            case 'Esc':
                close(event);
                break;
            case 'Tab':
                /* istanbul ignore else */
                if (modal) {
                    const tabbableModalElements = tabbable(modal);
                    if (tabbableModalElements.length) {
                        const firstTabbableElement = tabbableModalElements[0];
                        const lastTabbableElement = tabbableModalElements[tabbableModalElements.length - 1];
                        if (event.shiftKey) {
                            if (document.activeElement === firstTabbableElement) {
                                event.preventDefault();
                                lastTabbableElement.focus();
                            }
                        }
                        else {
                            if (document.activeElement === lastTabbableElement) {
                                event.preventDefault();
                                firstTabbableElement.focus();
                            }
                        }
                    }
                    else {
                        event.preventDefault();
                    }
                }
        }
    };
    const onClickBackdrop = (event) => {
        if (event.target === event.currentTarget) {
            close(event);
        }
    };
    const onEntering = () => {
        hijackScrollAndPageAccess();
    };
    const onExited = () => {
        if (disablePageInteraction) {
            setDisablePageInteraction(false);
        }
        // Wait for page interaction to be enabled
        setTimeout(() => {
            var _a;
            (_a = context.trigger) === null || _a === void 0 ? void 0 : _a.focus();
        });
    };
    const BEM = (state) => {
        const classArray = ['modal', `is-${state}`];
        const modalSize = size ? size : sidebar ? '4-col' : undefined;
        if (size || sidebar) {
            classArray.push(`modal--${modalSize}`);
        }
        if (verticalAlign) {
            classArray.push(`modal--vertical-align-${verticalAlign}`);
        }
        if (isFullHeight) {
            classArray.push('modal--full-height');
        }
        if (isFullScreen) {
            classArray.push('modal--full-screen');
        }
        if (className) {
            classArray.push(className);
        }
        if (sidebar) {
            classArray.push(`modal--with-sidebar-${sidebar === true ? 'right' : sidebar}`);
        }
        return classArray.join(' ');
    };
    const renderHeaderOrFooter = (type) => {
        let element = null;
        React.Children.forEach(children, (child) => {
            if (React.isValidElement(child) && child.type === type) {
                element = child;
            }
        });
        return element;
    };
    const renderBody = () => {
        const bodyChildren = React.Children.toArray(children).filter(child => {
            return React.isValidElement(child) && child.type !== headerType && child.type !== ModalFooter;
        });
        return React.createElement(ModalContent, null, bodyChildren);
    };
    const shouldBeClosable = () => {
        return !!(isClosable && context.isClosable);
    };
    const isBackdropCloseEnabled = () => {
        return shouldBeClosable() && isClosable !== 'close-button' && context.isClosable !== 'close-button';
    };
    const renderCloseButton = () => {
        const { isClosable } = props;
        if (isClosable !== 'backdrop') {
            return (React.createElement("div", { className: "modal__close-wrapper" },
                React.createElement(CloseButton, { className: "modal__close", onClick: close, label: closeButtonLabel })));
        }
    };
    const renderModal = () => {
        const box = (React.createElement("div", { className: "modal__box" },
            asideContent,
            React.createElement("div", { className: "modal__box-inner" },
                shouldBeClosable() && renderCloseButton(),
                !hasContentComponent && renderHeaderOrFooter(headerType),
                !hasContentComponent && renderBody(),
                !hasContentComponent && renderHeaderOrFooter(ModalFooter),
                hasContentComponent && children)));
        const content = (state) => {
            return (React.createElement("div", { className: BEM(state), id: context.id, onKeyDown: onKeyDown, tabIndex: -1, ref: element => (modal = element), role: "dialog", "aria-modal": true, "aria-labelledby": props['aria-labelledby'], "aria-describedby": props['aria-describedby'], 
                // Prevent click events from propagating to portal parents
                onClick: e => e.stopPropagation() },
                React.createElement("div", { className: "modal__backdrop", onClick: isBackdropCloseEnabled() ? onClickBackdrop : undefined }),
                React.createElement("div", { className: "modal__inner" }, isMounted ? React.createElement(React.Suspense, { fallback: React.createElement(Spinner, { className: "modal__spinner" }) }, box) : box)));
        };
        const { BodyScroll, VisibilityProvider, PageAccess } = props.components;
        return (React.createElement(VisibilityProvider, { isVisible: isModalOpen() },
            React.createElement(PageAccess, { isInert: disablePageInteraction },
                React.createElement(BodyScroll, { isDisabled: disablePageInteraction },
                    React.createElement(Transition, { in: isModalOpen(), timeout: { enter: 300, exit: 300 }, mountOnEnter: true, unmountOnExit: true, onEntering: onEntering, onExited: onExited }, content)))));
    };
    return isMounted ? ReactDOM.createPortal(renderModal(), portalRoot || document.body) : renderModal();
};
export { ModalCore as default };
